// --- React
import React, { useMemo } from 'react';
// --- Utils
import { getGroupedSummaryDetails } from '../utils/getDetailsData';
// --- Components
import { FieldRow } from '../../components';
// --- Styles
import '../../styles.scss';

/**
 * SummaryCard
 *
 * Features:
 * - Displays summary details in grouped sections (tap-ui style)
 * - Horizontal label-value layout
 * - Supports search/filter functionality
 * - All original data points preserved
 *
 * @param {object} details - Product details object
 * @param {string} searchTerm - Optional search term to filter fields
 * @returns {Element}
 */
export function SummaryCard({ details, searchTerm = '' }) {
    // Get grouped summary details
    const groupedDetails = useMemo(() => {
        return getGroupedSummaryDetails(details);
    }, [details]);

    // Filter groups and fields based on search term
    const filteredGroups = useMemo(() => {
        if (!searchTerm.trim()) return groupedDetails;

        const term = searchTerm.toLowerCase();
        return groupedDetails
            .map((group) => ({
                ...group,
                fields: group.fields.filter(
                    (field) =>
                        field.label.toLowerCase().includes(term) ||
                        String(field.value || '').toLowerCase().includes(term)
                ),
            }))
            .filter((group) => group.fields.length > 0);
    }, [groupedDetails, searchTerm]);

    if (filteredGroups.length === 0) {
        return (
            <div className="no-results">
                <i className="fa-solid fa-search" />
                <p>No fields match your search.</p>
            </div>
        );
    }

    return (
        <div className="summary-card">
            {filteredGroups.map((group) => (
                <div key={group.key} className="detail-section">
                    <div className="detail-section-header">
                        <span className="detail-section-title">{group.title}</span>
                    </div>
                    <div className="detail-section-content">
                        {group.fields.map((field, index) => (
                            <FieldRow
                                key={index}
                                label={field.label}
                                value={field.value}
                                noBorder={index === group.fields.length - 1}
                            />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}
